import { api } from "@/lib/api";
import type {
  ArticleDetail,
  ArticleListItem,
  BannerPlacement,
  Category,
  ClientBanner,
  ContactPayload,
  LeadPayload,
  Paginated,
  Plan,
  SiteSettings,
  Tag,
} from "@/types/api";

export async function fetchSiteSettings(): Promise<SiteSettings> {
  const { data } = await api.get<SiteSettings>("/site/");
  return data;
}

export async function fetchCategories(): Promise<Category[]> {
  const { data } = await api.get<Paginated<Category>>("/categories/");
  return data.results;
}

export async function fetchCategory(slug: string): Promise<Category> {
  const { data } = await api.get<Category>(`/categories/${slug}/`);
  return data;
}

export async function fetchTags(): Promise<Tag[]> {
  const { data } = await api.get<Paginated<Tag>>("/tags/");
  return data.results;
}

export async function fetchTag(slug: string): Promise<Tag> {
  const { data } = await api.get<Tag>(`/tags/${slug}/`);
  return data;
}

export async function fetchArticles(params?: Record<string, string | number | boolean>) {
  const { data } = await api.get<Paginated<ArticleListItem>>("/articles/", { params });
  return data;
}

export async function fetchCategoryArticles(slug: string, params?: Record<string, string>) {
  const { data } = await api.get<Paginated<ArticleListItem>>(`/categories/${slug}/articles/`, {
    params,
  });
  return data;
}

export async function fetchTagArticles(slug: string, params?: Record<string, string>) {
  const { data } = await api.get<Paginated<ArticleListItem>>(`/tags/${slug}/articles/`, {
    params,
  });
  return data;
}

export async function fetchArticle(slug: string): Promise<ArticleDetail> {
  const { data } = await api.get<ArticleDetail>(`/articles/${slug}/`);
  return data;
}

export async function searchArticles(
  q: string,
  params?: Record<string, string | number>,
): Promise<Paginated<ArticleListItem>> {
  const { data } = await api.get<Paginated<ArticleListItem>>("/search/", {
    params: { q, ...params },
  });
  return data;
}

export async function fetchPlans(): Promise<Plan[]> {
  const { data } = await api.get<Paginated<Plan>>("/plans/");
  return data.results;
}

export async function submitContact(payload: ContactPayload) {
  const { data } = await api.post("/contact/", payload);
  return data;
}

export async function submitLead(payload: LeadPayload) {
  const { data } = await api.post("/leads/", payload);
  return data;
}

export async function fetchClientBanners(placement?: BannerPlacement): Promise<ClientBanner[]> {
  const { data } = await api.get<ClientBanner[] | Paginated<ClientBanner>>("/banners/", {
    params: placement ? { placement } : undefined,
  });
  if (Array.isArray(data)) return data;
  return data.results ?? [];
}

export async function trackBannerClick(
  bannerId: number,
  button: "website" | "whatsapp" | "phone",
) {
  await api.post(`/banners/${bannerId}/click/`, { button });
}
